import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, ScanLine, FileCheck2, MailCheck, PiggyBank, Check, Pause, Play } from "lucide-react";

const Row = ({ a, b, tone = "", d = 0 }) => (
  <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: d }} className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs ${tone || "border-slate-100 bg-slate-50"}`}>
    <span className="truncate">{a}</span><span className="shrink-0 font-mono font-semibold">{b}</span>
  </motion.div>
);

const Snap = () => (
  <div className="relative mx-auto h-full w-44 rounded-[28px] border-[6px] border-[#1E293B] bg-[#0B1220] p-3 shadow-xl">
    <div className="paper h-full rounded-2xl p-3 shadow-inner">
      <p className="text-[10px] font-bold text-slate-500">NORTHFIELD WHOLESALE</p>
      <p className="mt-1 font-mono text-[10px]">INVOICE A1002</p>
      {[1, 2, 3, 4].map((i) => <div key={i} className="mt-2 h-1.5 rounded bg-slate-200" style={{ width: `${90 - i * 12}%` }} />)}
      <p className="mt-3 text-right font-mono text-[11px] font-bold">£112.50</p>
    </div>
    <motion.div className="paper absolute inset-3 rounded-2xl" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.9, 0] }} transition={{ delay: 1.4, duration: 0.5 }} />
    <motion.div className="absolute -bottom-5 left-1/2 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg" animate={{ scale: [1, 0.85, 1] }} transition={{ delay: 1.2, duration: 0.4 }}><Camera className="h-5 w-5" /></motion.div>
  </div>
);

const Read = () => (
  <div className="relative space-y-2">
    <motion.div className="absolute inset-x-0 h-0.5 bg-teal-500 shadow-[0_0_12px_#14b8a6]" initial={{ top: 0 }} animate={{ top: "100%" }} transition={{ duration: 2.2, ease: "easeInOut" }} />
    <Row a="Wholesaler" b="Northfield" d={0.3} /><Row a="Invoice number" b="A1002" d={0.7} /><Row a="Date" b="03 Jun 2026" d={1.1} /><Row a="Total" b="£112.50" d={1.5} tone="border-teal-200 bg-teal-50 text-teal-900" />
  </div>
);

const Match = () => (
  <div className="space-y-2">
    <Row a="A1001 · invoice £86.20 · statement £86.20" b="✓" tone="border-emerald-200 bg-emerald-50 text-emerald-800" d={0.2} />
    <Row a="A1002 · invoice £112.50 · statement £120.00" b="£7.50" tone="border-amber-200 bg-amber-50 text-amber-800" d={0.7} />
    <Row a="Credit C501 · not on statement" b="£15.00" tone="border-amber-200 bg-amber-50 text-amber-800" d={1.2} />
    <Row a="A1003 · invoice £240.00 · statement £240.00" b="✓" tone="border-emerald-200 bg-emerald-50 text-emerald-800" d={1.7} />
  </div>
);

const Query = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs shadow-sm">
    <p className="text-slate-500">To: accounts@wholesaler · Subject: <span className="font-mono text-teal-700">[QRY-00412]</span> Amount differs</p>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-3">Hello, invoice A1002 shows £112.50 but your statement charges £120.00. Please credit £7.50.</motion.p>
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1.5 font-semibold text-white"><Check className="h-3.5 w-3.5" />Sent · reminder every 3 days</motion.div>
  </div>
);

const Recover = () => (
  <div className="text-center">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Credited this month</p>
    <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 160, damping: 12 }} className="mt-2 font-display text-6xl font-extrabold text-teal-700">£82.50</motion.p>
    <div className="mt-4 flex justify-center gap-2 text-[11px]">{["QRY-00412 £7.50", "QRY-00413 £15.00", "QRY-00414 £60.00"].map((t, i) => <motion.span key={t} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.25 }} className="rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-emerald-800">{t}</motion.span>)}</div>
  </div>
);

const SCENES = [
  { icon: Camera, t: "Snap", d: "Staff photograph the invoice on a phone", C: Snap },
  { icon: ScanLine, t: "Read", d: "AI reads the wholesaler, number, date and total", C: Read },
  { icon: FileCheck2, t: "Match", d: "Every statement line is checked against your invoices", C: Match },
  { icon: MailCheck, t: "Query", d: "Each problem is emailed to the wholesaler and chased", C: Query },
  { icon: PiggyBank, t: "Recover", d: "Credits are tracked until they reach your account", C: Recover },
];

export default function Walkthrough() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setI((x) => (x + 1) % SCENES.length), 4000);
    return () => clearTimeout(t);
  }, [i, paused]);
  const S = SCENES[i];
  return (
    <div id="walkthrough" className="scroll-mt-28 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_40px_120px_-30px_rgba(16,185,129,0.45)]" data-testid="walkthrough-section">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div className="flex gap-1.5">{["bg-rose-300", "bg-amber-300", "bg-emerald-300"].map((c) => <span key={c} className={`h-2.5 w-2.5 rounded-full ${c}`} />)}</div>
        <p className="text-xs font-semibold text-slate-500">How it works · 20 seconds</p>
        <button onClick={() => setPaused(!paused)} className="text-slate-400 hover:text-slate-900" aria-label={paused ? "Play" : "Pause"} data-testid="walkthrough-pause-button">{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</button>
      </div>
      <div className="relative h-72 bg-gradient-to-b from-emerald-500/[0.07] to-transparent px-6 py-8 sm:h-80">
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }} className="mx-auto flex h-full max-w-md flex-col justify-center" data-testid={`walkthrough-scene-${i + 1}`}>
            <S.C />
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="px-5 pb-5">
        <p className="mb-3 text-center text-sm font-semibold text-slate-700" data-testid="walkthrough-caption">{S.d}</p>
        <div className="grid grid-cols-5 gap-2">
          {SCENES.map(({ icon: I, t }, k) => (
            <button key={t} onClick={() => { setI(k); setPaused(true); }} className={`group rounded-xl px-1 py-2 text-[11px] font-semibold transition-colors ${k === i ? "bg-teal-50 text-teal-800" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`walkthrough-step-btn-${k + 1}`}>
              <I className="mx-auto mb-1 h-4 w-4" />{t}
              <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-slate-100">
                {k === i && <motion.span key={`${i}-${paused}`} className="block h-full bg-teal-600" initial={{ width: paused ? "100%" : 0 }} animate={{ width: "100%" }} transition={{ duration: paused ? 0 : 4, ease: "linear" }} />}
                {k < i && <span className="block h-full bg-teal-600/40" />}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
